import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  ChevronLeft, ChevronRight, Save, X,
  Megaphone, Sparkles, Gift, Bell, Star, Rocket, Heart,
  Zap, Music, PartyPopper, Trophy, Info, Calendar, Volume2,
  TrendingUp, Award, Flame, Crown, Mic2,
} from 'lucide-react';

const ICON_MAP = {
  Megaphone, Sparkles, Gift, Bell, Star, Rocket, Heart,
  Zap, Music, PartyPopper, Trophy, Info, Calendar, Volume2,
  TrendingUp, Award, Flame, Crown, Mic2,
};

const ICON_OPTIONS = [
  { value: '', label: '기본 (메가폰)' },
  { value: 'Megaphone', label: '메가폰' },
  { value: 'Sparkles', label: '반짝임' },
  { value: 'Gift', label: '선물' },
  { value: 'Bell', label: '알림' },
  { value: 'Star', label: '별' },
  { value: 'Rocket', label: '로켓' },
  { value: 'Heart', label: '하트' },
  { value: 'Zap', label: '번개' },
  { value: 'Music', label: '음악' },
  { value: 'PartyPopper', label: '축하' },
  { value: 'Trophy', label: '트로피' },
  { value: 'Info', label: '정보' },
  { value: 'Calendar', label: '캘린더' },
  { value: 'Volume2', label: '스피커' },
  { value: 'TrendingUp', label: '성장' },
  { value: 'Award', label: '수상' },
  { value: 'Flame', label: '불꽃' },
  { value: 'Crown', label: '왕관' },
  { value: 'Mic2', label: '마이크' },
];

const THEMES = [
  { value: 'blue', label: 'Blue', accent: '#38bdf8' },
  { value: 'purple', label: 'Purple', accent: '#a78bfa' },
  { value: 'pink', label: 'Pink', accent: '#f472b6' },
  { value: 'green', label: 'Green', accent: '#34d399' },
  { value: 'orange', label: 'Orange', accent: '#fbbf24' },
  { value: 'dark', label: 'Dark', accent: '#cbd5e1' },
];

const TARGET_PAGES = [
  { value: 'all', label: '전체' },
  { value: 'homepage', label: '홈페이지' },
  { value: 'settlement', label: '정산시스템' },
];

const emptyForm = { title: '', content: '', theme: 'blue', targetPage: 'all', icon: '', startDate: '', endDate: '' };

export default function AdminPopupPage() {
  const [list, setList] = useState({ popups: [], total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/popup/list?page=${page}`);
      setList(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (popup) => {
    setEditId(popup.id);
    setForm({
      title: popup.title,
      content: popup.content,
      theme: popup.theme || 'blue',
      targetPage: popup.targetPage || 'all',
      icon: popup.icon || '',
      startDate: popup.startDate || '',
      endDate: popup.endDate || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return alert('제목을 입력해주세요.');
    if (!form.content.trim()) return alert('내용을 입력해주세요.');
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`/api/popup/${editId}`, form);
      } else {
        await axios.post('/api/popup', form);
      }
      setShowForm(false);
      setForm(emptyForm);
      setEditId(null);
      fetchList(list.page);
    } catch (err) {
      alert(err.response?.data?.errorMessage || '저장에 실패했습니다.');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await axios.delete(`/api/popup/${id}`);
      fetchList(list.page);
    } catch (err) {
      alert(err.response?.data?.errorMessage || '삭제에 실패했습니다.');
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`/api/popup/${id}/toggle`);
      fetchList(list.page);
    } catch (err) {
      alert(err.response?.data?.errorMessage || '변경에 실패했습니다.');
    }
  };

  const getThemeInfo = (val) => THEMES.find((t) => t.value === val) || THEMES[0];
  const accent = getThemeInfo(form.theme).accent;
  const PreviewIcon = (form.icon && ICON_MAP[form.icon]) || Megaphone;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">팝업 관리</h1>
          <p className="text-slate-500 text-xs mt-1">홈페이지/정산시스템에 표시되는 팝업을 관리합니다.</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-sky-500/20 text-sky-400 border border-sky-500/30 hover:bg-sky-500/30 transition-colors"
        >
          <Plus size={15} />
          새 팝업
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
            <h2 className="text-white font-bold">{editId ? '팝업 수정' : '새 팝업 등록'}</h2>
            <button
              onClick={() => { setShowForm(false); setEditId(null); setForm(emptyForm); }}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <div className="px-6 py-5 space-y-4">
            {/* Title */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">제목</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={200}
                placeholder="팝업 제목을 입력하세요"
                className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white placeholder-slate-600 text-sm focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20"
              />
            </div>

            {/* Content */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">내용</label>
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={5}
                placeholder="팝업 내용을 입력하세요"
                className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white placeholder-slate-600 text-sm focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 resize-none"
              />
            </div>

            {/* Theme + Icon row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Theme */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">테마</label>
                <div className="flex flex-wrap gap-1.5">
                  {THEMES.map(({ value, label, accent: ac }) => (
                    <button
                      key={value}
                      onClick={() => setForm({ ...form, theme: value })}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                        form.theme === value
                          ? 'ring-2 ring-white/30 bg-white/[0.08] text-white'
                          : 'bg-white/[0.03] text-slate-500 hover:bg-white/[0.06] hover:text-slate-300'
                      }`}
                    >
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: ac }} />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Icon */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">아이콘</label>
                <div className="flex flex-wrap gap-1">
                  {ICON_OPTIONS.map(({ value, label }) => {
                    const Ic = value ? ICON_MAP[value] : Megaphone;
                    return (
                      <button
                        key={value}
                        onClick={() => setForm({ ...form, icon: value })}
                        title={label}
                        className={`p-2 rounded-lg transition-all ${
                          form.icon === value
                            ? 'ring-2 ring-white/30 bg-white/[0.1] text-white'
                            : 'bg-white/[0.03] text-slate-500 hover:bg-white/[0.06] hover:text-slate-300'
                        }`}
                      >
                        <Ic size={16} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Target Page */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">표시 페이지</label>
              <div className="flex gap-2">
                {TARGET_PAGES.map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setForm({ ...form, targetPage: value })}
                    className={`px-4 py-2 rounded-xl text-xs font-medium transition-all ${
                      form.targetPage === value
                        ? 'ring-2 ring-sky-500/30 bg-sky-500/15 text-sky-400'
                        : 'bg-white/[0.03] text-slate-500 hover:bg-white/[0.06] hover:text-slate-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">시작일 (선택)</label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 [color-scheme:dark]"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">종료일 (선택)</label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Preview — 실제 팝업과 동일한 글라스모피즘 */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">미리보기</label>
              <div className="relative flex items-center justify-center rounded-2xl overflow-hidden py-10 px-6" style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)' }}>
                {/* Ambient glow behind */}
                <div
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full opacity-15 blur-3xl"
                  style={{ backgroundColor: accent }}
                />
                {/* Glass card */}
                <div
                  className="relative w-full max-w-md rounded-3xl overflow-hidden"
                  style={{
                    background: 'rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(24px)',
                    WebkitBackdropFilter: 'blur(24px)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 80px ${accent}10, inset 0 1px 0 rgba(255, 255, 255, 0.1)`,
                  }}
                >
                  {/* Top accent glow */}
                  <div
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px"
                    style={{ background: `linear-gradient(90deg, transparent, ${accent}80, transparent)` }}
                  />
                  {/* Close btn placeholder */}
                  <div className="absolute top-4 right-4 p-1.5 rounded-full text-white/30">
                    <X size={18} />
                  </div>
                  {/* Content */}
                  <div className="px-7 pt-7 pb-5">
                    <div className="flex items-start justify-between mb-5">
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${accent}20, ${accent}08)`,
                          border: `1px solid ${accent}25`,
                          boxShadow: `0 4px 16px ${accent}15`,
                        }}
                      >
                        <PreviewIcon size={26} style={{ color: accent }} />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white leading-tight mb-2">{form.title || '제목 미리보기'}</h3>
                    <div
                      className="w-8 h-[3px] rounded-full mb-4"
                      style={{ background: `linear-gradient(90deg, ${accent}, ${accent}40)` }}
                    />
                    <p className="text-white/60 text-sm whitespace-pre-wrap line-clamp-4">{form.content || '내용이 여기에 표시됩니다.'}</p>
                  </div>
                  {/* Bottom buttons */}
                  <div
                    className="px-7 py-4 flex items-center gap-2"
                    style={{ background: 'rgba(0, 0, 0, 0.25)', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}
                  >
                    <span className="flex-1 px-3 py-2.5 rounded-xl text-xs font-medium text-white/60 text-center" style={{ background: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>오늘 그만보기</span>
                    <span className="flex-1 px-3 py-2.5 rounded-xl text-xs font-medium text-white/60 text-center" style={{ background: 'rgba(255, 255, 255, 0.08)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>7일간 안보기</span>
                    <span className="flex-1 px-3 py-2.5 rounded-xl text-xs font-semibold text-center" style={{ background: `${accent}30`, color: accent, border: `1px solid ${accent}40` }}>닫기</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Save */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold bg-sky-500/20 text-sky-400 border border-sky-500/30 hover:bg-sky-500/30 transition-colors disabled:opacity-50"
              >
                <Save size={15} />
                {saving ? '저장 중...' : editId ? '수정' : '등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/[0.06]">
          <h2 className="text-white font-bold">팝업 목록</h2>
          <p className="text-slate-500 text-xs mt-0.5">총 {list.total}개</p>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">불러오는 중...</div>
        ) : list.popups.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">등록된 팝업이 없습니다.</div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {list.popups.map((popup) => {
              const ti = getThemeInfo(popup.theme);
              const ListIcon = (popup.icon && ICON_MAP[popup.icon]) || Megaphone;
              return (
                <div key={popup.id} className="px-6 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition-colors">
                  {/* Icon chip */}
                  <div
                    className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center mt-0.5"
                    style={{
                      background: `linear-gradient(135deg, ${ti.accent}20, ${ti.accent}08)`,
                      border: `1px solid ${ti.accent}20`,
                    }}
                  >
                    <ListIcon size={18} style={{ color: ti.accent }} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-semibold text-sm truncate">{popup.title}</span>
                      <span
                        className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          popup.isActive
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-slate-500/20 text-slate-500'
                        }`}
                      >
                        {popup.isActive ? '활성' : '비활성'}
                      </span>
                    </div>
                    <p className="text-slate-500 text-xs line-clamp-1 mb-1">{popup.content}</p>
                    <div className="flex items-center gap-3 text-[11px] text-slate-600">
                      <span>{ti.label}</span>
                      <span>{TARGET_PAGES.find(t => t.value === popup.targetPage)?.label || '전체'}</span>
                      {popup.startDate && <span>시작: {popup.startDate}</span>}
                      {popup.endDate && <span>종료: {popup.endDate}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleToggle(popup.id)}
                      title={popup.isActive ? '비활성화' : '활성화'}
                      className={`p-1.5 rounded-lg transition-colors ${
                        popup.isActive
                          ? 'text-emerald-400 hover:bg-emerald-500/10'
                          : 'text-slate-500 hover:bg-white/10'
                      }`}
                    >
                      {popup.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                    </button>
                    <button
                      onClick={() => openEdit(popup)}
                      title="수정"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(popup.id)}
                      title="삭제"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {list.totalPages > 1 && (
          <div className="px-6 py-3 border-t border-white/[0.04] flex items-center justify-center gap-2">
            <button
              onClick={() => fetchList(list.page - 1)}
              disabled={list.page <= 1}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs text-slate-500 min-w-[80px] text-center">
              {list.page} / {list.totalPages}
            </span>
            <button
              onClick={() => fetchList(list.page + 1)}
              disabled={list.page >= list.totalPages}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
